import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
} from "react";
import { getApiUrl } from "../config/api";
import { useAuth } from "./AuthContext";
import { isExpiredExpiryYmd } from "../utils/analyticsContractChain";

/** One point in time captured on each successful API refresh (max 60 per contract). */
export interface TrackedMetricPoint {
    at: string;
    price?: number;
    volume?: number;
    oi?: number;
    bid?: number;
    ask?: number;
    delta?: number;
    gamma?: number;
    theta?: number;
    vega?: number;
    iv?: number;
    premium?: number;
}

export interface TrackedContract {
    id: string;
    symbol: string;
    strike: number;
    expiry: string; // YYYY-MM-DD
    optionType: "C" | "P";
    addedAt: string;
    // ── baseline snapshot (captured at add time) ──
    snapshotPrice?: number;
    snapshotVolume?: number;
    snapshotOI?: number;
    snapshotBid?: number;
    snapshotAsk?: number;
    snapshotDelta?: number;
    snapshotGamma?: number;
    snapshotTheta?: number;
    snapshotVega?: number;
    snapshotIV?: number;
    snapshotPremium?: number;
    // ── values at start of last refresh (for ~ since last poll deltas) ──
    pollPrice?: number;
    pollVolume?: number;
    pollOI?: number;
    pollBid?: number;
    pollAsk?: number;
    pollDelta?: number;
    pollGamma?: number;
    pollTheta?: number;
    pollVega?: number;
    pollIV?: number;
    pollPremium?: number;
    pollUpdatedAt?: string;
    // ── latest values ──
    lastPrice?: number;
    lastVolume?: number;
    lastOI?: number;
    lastBid?: number;
    lastAsk?: number;
    lastDelta?: number;
    lastGamma?: number;
    lastTheta?: number;
    lastVega?: number;
    lastIV?: number;
    lastPremium?: number;
    lastSide?: string;
    lastUpdated?: string;
    lastSource?: string;
    lastChangedFields?: string[];
    /** Rolling history from API refreshes (not stable-bar merges). */
    metricHistory?: TrackedMetricPoint[];
    // ── accumulated flow tracking (RTL-style: accumulate bids/asks over time) ──
    accBidVolume?: number;
    accAskVolume?: number;
    accBidPremium?: number;
    accAskPremium?: number;
    accDeltaPremium?: number;
}

const MAX_TRACKED = 50;
const MAX_HISTORY = 60;
const STORAGE_KEY = "mmec_tracked_contracts";
const REFRESH_MS = 30_000;
/** Abort hung status calls so the tab does not hoard connections. */
const REQUEST_TIMEOUT_MS = 45_000;
const FAIL_BACKOFF_BASE_MS = 60_000;
const FAIL_BACKOFF_MAX_MS = 300_000;

interface ContractTrackingContextType {
    trackedContracts: TrackedContract[];
    addContract: (contract: Omit<TrackedContract, "id" | "addedAt">) => boolean;
    removeContract: (id: string) => void;
    isTracked: (
        symbol: string,
        strike: number,
        expiry: string,
        optionType: string,
    ) => boolean;
    getTrackedCount: () => number;
    refreshStatus: (manual?: boolean) => Promise<void>;
    refreshFromData: (rows: any[]) => void;
    isRefreshing: boolean;
    lastRefreshAt: string | null;
    lastRefreshError: string | null;
    exportContracts: () => string;
    importContracts: (json: string) => { added: number; skipped: number };
    removeExpired: () => number;
}

const ContractTrackingContext = createContext<
    ContractTrackingContextType | undefined
>(undefined);

export const useContractTracking = () => {
    const context = useContext(ContractTrackingContext);
    if (!context)
        throw new Error(
            "useContractTracking must be used within ContractTrackingProvider",
        );
    return context;
};

function makeContractId(
    symbol: string,
    strike: number,
    expiry: string,
    optionType: string,
): string {
    return `${symbol.toUpperCase()}|${strike}|${expiry}|${optionType.toUpperCase()}`;
}

function num(v: unknown): number | undefined {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
}

function capturePollFromLast(c: TrackedContract): TrackedContract {
    return {
        ...c,
        pollPrice: c.lastPrice,
        pollVolume: c.lastVolume,
        pollOI: c.lastOI,
        pollBid: c.lastBid,
        pollAsk: c.lastAsk,
        pollDelta: c.lastDelta,
        pollGamma: c.lastGamma,
        pollTheta: c.lastTheta,
        pollVega: c.lastVega,
        pollIV: c.lastIV,
        pollPremium: c.lastPremium,
        pollUpdatedAt: c.lastUpdated ?? new Date().toISOString(),
    };
}

function fillSnapshotsIfMissing(
    c: TrackedContract,
    values: Partial<TrackedContract>,
): TrackedContract {
    return {
        ...c,
        snapshotPrice: c.snapshotPrice ?? values.lastPrice,
        snapshotVolume: c.snapshotVolume ?? values.lastVolume,
        snapshotOI: c.snapshotOI ?? values.lastOI,
        snapshotBid: c.snapshotBid ?? values.lastBid,
        snapshotAsk: c.snapshotAsk ?? values.lastAsk,
        snapshotDelta: c.snapshotDelta ?? values.lastDelta,
        snapshotGamma: c.snapshotGamma ?? values.lastGamma,
        snapshotTheta: c.snapshotTheta ?? values.lastTheta,
        snapshotVega: c.snapshotVega ?? values.lastVega,
        snapshotIV: c.snapshotIV ?? values.lastIV,
        snapshotPremium: c.snapshotPremium ?? values.lastPremium,
    };
}

function appendMetricHistory(
    c: TrackedContract,
    point: TrackedMetricPoint,
): TrackedContract {
    const prev = c.metricHistory ?? [];
    const next = [...prev, point].slice(-MAX_HISTORY);
    return { ...c, metricHistory: next };
}

function statusRowToValues(status: Record<string, unknown>): Partial<TrackedContract> {
    return {
        lastPrice: num(status.lastPrice ?? status.last_price),
        lastVolume: num(status.lastVolume ?? status.last_volume),
        lastOI: num(status.lastOI ?? status.last_oi),
        lastBid: num(status.lastBid ?? status.last_bid),
        lastAsk: num(status.lastAsk ?? status.last_ask),
        lastDelta: num(status.lastDelta ?? status.last_delta),
        lastGamma: num(status.lastGamma ?? status.last_gamma),
        lastTheta: num(status.lastTheta ?? status.last_theta),
        lastVega: num(status.lastVega ?? status.last_vega),
        lastIV: num(status.lastIV ?? status.last_iv),
        lastPremium: num(status.lastPremium ?? status.last_premium),
        lastSide: String(status.lastSide ?? status.last_side ?? ""),
        lastUpdated: String(
            status.lastUpdated ?? status.last_updated ?? new Date().toISOString(),
        ),
        lastSource: String(status.source ?? status.lastSource ?? ""),
    };
}

function migrateTrackedContract(c: TrackedContract): TrackedContract {
    return {
        ...c,
        metricHistory: Array.isArray(c.metricHistory) ? c.metricHistory : [],
    };
}

function withoutExpiredContracts(
    contracts: TrackedContract[],
): TrackedContract[] {
    return contracts.filter((c) => !isExpiredExpiryYmd(c.expiry));
}

function persistTrackedIfShrunk(
    prev: TrackedContract[],
    next: TrackedContract[],
): TrackedContract[] {
    if (next.length === prev.length) return prev;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    return next;
}

function friendlyRefreshError(
    err: unknown,
    responseStatus?: number,
): string {
    if (responseStatus === 429) {
        return "Watchlist refresh paused (rate limit) — your last data is still shown.";
    }
    if (responseStatus && responseStatus >= 500) {
        return "Server busy — watchlist will retry automatically. Rest of the app is unaffected.";
    }
    const msg =
        err instanceof Error
            ? err.message
            : typeof err === "string"
              ? err
              : "Refresh failed";
    if (
        /insufficient|failed to fetch|networkerror|load failed|aborted|timeout/i.test(
            msg,
        )
    ) {
        return "Watchlist refresh unavailable — your saved contracts are unchanged. Retrying with backoff.";
    }
    return msg;
}

export const ContractTrackingProvider: React.FC<{
    children: React.ReactNode;
}> = ({ children }) => {
    const { token } = useAuth();
    const [tracked, setTracked] = useState<TrackedContract[]>([]);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshAt, setLastRefreshAt] = useState<string | null>(null);
    const [lastRefreshError, setLastRefreshError] = useState<string | null>(
        null,
    );
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const rateLimitUntilRef = useRef(0);
    const backoffUntilRef = useRef(0);
    const failStreakRef = useRef(0);
    const refreshInFlightRef = useRef(false);
    const abortRef = useRef<AbortController | null>(null);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                const parsed = JSON.parse(stored) as TrackedContract[];
                if (Array.isArray(parsed)) {
                    const migrated = parsed.map(migrateTrackedContract);
                    const kept = withoutExpiredContracts(migrated);
                    setTracked(kept);
                    if (kept.length !== migrated.length) {
                        localStorage.setItem(STORAGE_KEY, JSON.stringify(kept));
                    }
                }
            }
        } catch {}
    }, []);

    const purgeExpiredFromState = useCallback((): number => {
        let removed = 0;
        setTracked((prev) => {
            if (!prev.length) return prev;
            const kept = withoutExpiredContracts(prev);
            removed = prev.length - kept.length;
            return persistTrackedIfShrunk(prev, kept);
        });
        return removed;
    }, []);

    useEffect(() => {
        const id = setInterval(() => {
            purgeExpiredFromState();
        }, 60_000);
        return () => clearInterval(id);
    }, [purgeExpiredFromState]);

    const addContract = useCallback(
        (contract: Omit<TrackedContract, "id" | "addedAt">): boolean => {
            const id = makeContractId(
                contract.symbol,
                contract.strike,
                contract.expiry,
                contract.optionType,
            );

            if (isExpiredExpiryYmd(contract.expiry)) {
                return false;
            }

            let added = false;
            setTracked((prev) => {
                if (prev.length >= MAX_TRACKED) {
                    alert(
                        `Maximum ${MAX_TRACKED} contracts tracked. Remove some first.`,
                    );
                    return prev;
                }
                if (prev.some((c) => c.id === id)) return prev;

                const lp = num(contract.lastPrice);
                const lv = num(contract.lastVolume);
                const loi = num(contract.lastOI);
                const lbid = num(contract.lastBid);
                const lask = num(contract.lastAsk);
                const ld = num(contract.lastDelta);
                const lg = num(contract.lastGamma);
                const lt = num(contract.lastTheta);
                const lve = num(contract.lastVega);
                const liv = num(contract.lastIV);
                const lpr = num(contract.lastPremium);

                const entry: TrackedContract = {
                    ...contract,
                    id,
                    addedAt: new Date().toISOString(),
                    snapshotPrice: num(contract.snapshotPrice) ?? lp,
                    snapshotVolume: num(contract.snapshotVolume) ?? lv,
                    snapshotOI: num(contract.snapshotOI) ?? loi,
                    snapshotBid: num(contract.snapshotBid) ?? lbid,
                    snapshotAsk: num(contract.snapshotAsk) ?? lask,
                    snapshotDelta: num(contract.snapshotDelta) ?? ld,
                    snapshotGamma: num(contract.snapshotGamma) ?? lg,
                    snapshotTheta: num(contract.snapshotTheta) ?? lt,
                    snapshotVega: num(contract.snapshotVega) ?? lve,
                    snapshotIV: num(contract.snapshotIV) ?? liv,
                    snapshotPremium: num(contract.snapshotPremium) ?? lpr,
                    lastPrice: lp,
                    lastVolume: lv,
                    lastOI: loi,
                    lastBid: lbid,
                    lastAsk: lask,
                    lastDelta: ld,
                    lastGamma: lg,
                    lastTheta: lt,
                    lastVega: lve,
                    lastIV: liv,
                    lastPremium: lpr,
                    lastUpdated: new Date().toISOString(),
                    metricHistory: [],
                };
                added = true;
                const updated = [...prev, entry];
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
                return updated;
            });
            return added;
        },
        [],
    );

    const removeContract = useCallback((id: string) => {
        setTracked((prev) => {
            const updated = prev.filter((c) => c.id !== id);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            return updated;
        });
    }, []);

    const isTracked = useCallback(
        (
            symbol: string,
            strike: number,
            expiry: string,
            optionType: string,
        ): boolean => {
            const id = makeContractId(symbol, strike, expiry, optionType);
            return tracked.some((c) => c.id === id);
        },
        [tracked],
    );

    const getTrackedCount = useCallback(() => tracked.length, [tracked]);

    const applyStatusRows = useCallback((rows: any[]) => {
        if (!rows?.length) return;
        setTracked((prev) => {
            let changed = false;
            const byId = new Map<string, any>();
            for (const r of rows) {
                const id = String(r.id || "");
                if (id) byId.set(id, r);
            }
            const updated = prev.map((c) => {
                const status = byId.get(c.id);
                if (!status || status.status === "not_found") return c;

                const values = statusRowToValues(status);
                const withPoll = capturePollFromLast(c);
                let next: TrackedContract = fillSnapshotsIfMissing(withPoll, values);
                next = {
                    ...next,
                    ...values,
                    lastSide: values.lastSide || c.lastSide,
                    lastChangedFields: Array.isArray(status.changedFields)
                        ? status.changedFields
                        : [],
                };
                const histPoint: TrackedMetricPoint = {
                    at: values.lastUpdated ?? new Date().toISOString(),
                    price: values.lastPrice,
                    volume: values.lastVolume,
                    oi: values.lastOI,
                    bid: values.lastBid,
                    ask: values.lastAsk,
                    delta: values.lastDelta,
                    gamma: values.lastGamma,
                    theta: values.lastTheta,
                    vega: values.lastVega,
                    iv: values.lastIV,
                    premium: values.lastPremium,
                };
                next = appendMetricHistory(next, histPoint);
                changed = true;
                return next;
            });
            if (changed) {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
            }
            return updated;
        });
    }, []);

    const tryMergeRow = useCallback(
        (
            c: TrackedContract,
            row: Record<string, unknown>,
        ): TrackedContract | null => {
            const sym = String(
                row.Underlying ?? row.underlying ?? row.Underlying_Symbol ?? "",
            ).toUpperCase();
            const st = num(row.Strike ?? row["Strike Price"] ?? row.strike);
            const exp = String(
                row["Expiry Date"] ?? row.expiry_date ?? row.expiry ?? "",
            ).slice(0, 10);
            const otRaw = String(
                row["Option Type"] ?? row.optionType ?? row.option_type ?? "",
            ).toUpperCase();
            const ot = otRaw.startsWith("C")
                ? "C"
                : otRaw.startsWith("P")
                  ? "P"
                  : c.optionType;
            if (
                sym !== c.symbol.toUpperCase() ||
                st === undefined ||
                Math.abs(st - c.strike) >= 0.01 ||
                exp !== (c.expiry || "").slice(0, 10) ||
                ot !== c.optionType
            ) {
                return null;
            }

            const values: Partial<TrackedContract> = {
                lastPrice: num(row.Last ?? row.last ?? row.last_price),
                lastVolume: num(row.Volume ?? row.volume ?? row.total_volume),
                lastOI: num(row["Open Interest"] ?? row.open_interest ?? row.oi),
                lastBid: num(row.Bid ?? row.bid),
                lastAsk: num(row.Ask ?? row.ask),
                lastDelta: num(row.Delta ?? row.delta),
                lastGamma: num(row.Gamma ?? row.gamma),
                lastTheta: num(row.Theta ?? row.theta),
                lastVega: num(row.Vega ?? row.vega),
                lastIV: num(
                    row["Implied Volatility"] ??
                        row.implied_volatility ??
                        row.iv,
                ),
                lastPremium: num(
                    row.Premium ??
                        row.premium ??
                        row["Overall Premium"] ??
                        row["Delta Premium"],
                ),
                lastUpdated: new Date().toISOString(),
                lastSource: "stable_bar",
            };

            const withPoll = capturePollFromLast(c);
            return fillSnapshotsIfMissing(
                { ...withPoll, ...values },
                values,
            );
        },
        [],
    );

    const refreshStatus = useCallback(async (manual = false) => {
        const batch = await new Promise<TrackedContract[]>((resolve) => {
            setTracked((prev) => {
                const kept = withoutExpiredContracts(prev);
                const next = persistTrackedIfShrunk(prev, kept);
                resolve(kept);
                return next;
            });
        });
        if (!batch.length) return;
        if (refreshInFlightRef.current) return;
        if (!manual) {
            const now = Date.now();
            if (now < rateLimitUntilRef.current) return;
            if (now < backoffUntilRef.current) return;
            if (document.hidden) return;
        }

        refreshInFlightRef.current = true;
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const timeoutId = window.setTimeout(
            () => controller.abort(),
            REQUEST_TIMEOUT_MS,
        );

        setIsRefreshing(true);
        if (manual) setLastRefreshError(null);
        try {
            const headers: Record<string, string> = {
                "Content-Type": "application/json",
            };
            const authToken = tokenRef.current;
            if (authToken) headers.Authorization = `Bearer ${authToken}`;

            const response = await fetch(getApiUrl("/api/contracts/status"), {
                method: "POST",
                headers,
                signal: controller.signal,
                body: JSON.stringify({
                    contracts: batch.map((c) => ({
                        id: c.id,
                        symbol: c.symbol,
                        strike: c.strike,
                        expiry: c.expiry,
                        optionType: c.optionType,
                        lastPrice: c.lastPrice,
                        lastVolume: c.lastVolume,
                        lastOI: c.lastOI,
                        lastBid: c.lastBid,
                        lastAsk: c.lastAsk,
                        lastDelta: c.lastDelta,
                        lastGamma: c.lastGamma,
                        lastTheta: c.lastTheta,
                        lastVega: c.lastVega,
                        lastIV: c.lastIV,
                        lastPremium: c.lastPremium,
                    })),
                }),
            });
            if (!response.ok) {
                if (response.status === 429) {
                    rateLimitUntilRef.current = Date.now() + 60_000;
                } else if (!manual && response.status >= 500) {
                    failStreakRef.current += 1;
                    const backoff = Math.min(
                        FAIL_BACKOFF_MAX_MS,
                        FAIL_BACKOFF_BASE_MS * failStreakRef.current,
                    );
                    backoffUntilRef.current = Date.now() + backoff;
                }
                setLastRefreshError(
                    friendlyRefreshError(undefined, response.status),
                );
                return;
            }
            const json = await response.json();
            if (json?.success && Array.isArray(json.rows)) {
                applyStatusRows(json.rows);
                setLastRefreshAt(new Date().toISOString());
                setLastRefreshError(null);
                failStreakRef.current = 0;
                backoffUntilRef.current = 0;
            } else {
                setLastRefreshError(json?.error || "Invalid response");
            }
        } catch (e) {
            const aborted =
                e instanceof DOMException && e.name === "AbortError";
            if (!manual && (aborted || e instanceof TypeError)) {
                failStreakRef.current += 1;
                const backoff = Math.min(
                    FAIL_BACKOFF_MAX_MS,
                    FAIL_BACKOFF_BASE_MS * failStreakRef.current,
                );
                backoffUntilRef.current = Date.now() + backoff;
            }
            setLastRefreshError(
                aborted
                    ? "Watchlist refresh timed out — retrying later. Your last data is still shown."
                    : friendlyRefreshError(e),
            );
        } finally {
            window.clearTimeout(timeoutId);
            if (abortRef.current === controller) {
                abortRef.current = null;
            }
            refreshInFlightRef.current = false;
            setIsRefreshing(false);
        }
    }, [applyStatusRows]);

    useEffect(() => {
        if (!tracked.length) return;
        void refreshStatus();
        const interval = setInterval(() => {
            void refreshStatus();
        }, REFRESH_MS);
        return () => {
            clearInterval(interval);
            abortRef.current?.abort();
        };
    }, [tracked.length, refreshStatus]);

    useEffect(() => {
        const onVisible = () => {
            if (document.hidden) return;
            purgeExpiredFromState();
            if (!tracked.length) return;
            const stale =
                !lastRefreshAt ||
                Date.now() - Date.parse(lastRefreshAt) > REFRESH_MS;
            if (stale) void refreshStatus();
        };
        document.addEventListener("visibilitychange", onVisible);
        return () =>
            document.removeEventListener("visibilitychange", onVisible);
    }, [tracked.length, lastRefreshAt, refreshStatus, purgeExpiredFromState]);

    const exportContracts = useCallback((): string => {
        return JSON.stringify(tracked, null, 2);
    }, [tracked]);

    const importContracts = useCallback(
        (json: string): { added: number; skipped: number } => {
            let parsed: any[];
            try {
                parsed = JSON.parse(json);
            } catch {
                return { added: 0, skipped: 0 };
            }
            if (!Array.isArray(parsed)) return { added: 0, skipped: 0 };
            let added = 0,
                skipped = 0;
            setTracked((prev) => {
                const existing = new Set(prev.map((c) => c.id));
                const toAdd: TrackedContract[] = [];
                for (const item of parsed) {
                    if (
                        !item.symbol ||
                        item.strike == null ||
                        !item.expiry ||
                        !item.optionType
                    )
                        continue;
                    const id = makeContractId(
                        item.symbol,
                        item.strike,
                        item.expiry,
                        item.optionType,
                    );
                    if (existing.has(id)) {
                        skipped++;
                        continue;
                    }
                    const expiryYmd = String(item.expiry).slice(0, 10);
                    if (isExpiredExpiryYmd(expiryYmd)) {
                        skipped++;
                        continue;
                    }
                    if (prev.length + toAdd.length >= MAX_TRACKED) break;
                    const entry: TrackedContract = migrateTrackedContract({
                        symbol: String(item.symbol).toUpperCase(),
                        strike: Number(item.strike),
                        expiry: expiryYmd,
                        optionType: item.optionType === "P" ? "P" : "C",
                        id,
                        addedAt: item.addedAt || new Date().toISOString(),
                        snapshotPrice: num(item.snapshotPrice),
                        snapshotVolume: num(item.snapshotVolume),
                        snapshotOI: num(item.snapshotOI),
                        snapshotDelta: num(item.snapshotDelta),
                        lastPrice: num(item.lastPrice),
                        lastVolume: num(item.lastVolume),
                        lastOI: num(item.lastOI),
                        accBidVolume: num(item.accBidVolume) ?? 0,
                        accAskVolume: num(item.accAskVolume) ?? 0,
                    });
                    toAdd.push(entry);
                    added++;
                    existing.add(id);
                }
                if (toAdd.length === 0) return prev;
                const updated = [...prev, ...toAdd];
                localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
                return updated;
            });
            return { added, skipped };
        },
        [],
    );

    const removeExpired = useCallback((): number => {
        return purgeExpiredFromState();
    }, [purgeExpiredFromState]);

    const refreshFromData = useCallback(
        (rows: any[]) => {
            if (!rows?.length) return;
            setTracked((prev) => {
                let changed = false;
                const updated = prev.map((c) => {
                    for (const row of rows) {
                        const merged = tryMergeRow(
                            c,
                            row as Record<string, unknown>,
                        );
                        if (merged) {
                            changed = true;
                            return merged;
                        }
                    }
                    return c;
                });
                if (changed) {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
                }
                return updated;
            });
        },
        [tryMergeRow],
    );

    return (
        <ContractTrackingContext.Provider
            value={{
                trackedContracts: tracked,
                addContract,
                removeContract,
                isTracked,
                getTrackedCount,
                refreshStatus,
                refreshFromData,
                isRefreshing,
                lastRefreshAt,
                lastRefreshError,
                exportContracts,
                importContracts,
                removeExpired,
            }}
        >
            {children}
        </ContractTrackingContext.Provider>
    );
};
